import { Component, HostListener, signal, ElementRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { SiteConfigService } from '../../../core/config/site-config.service';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive],
  template: `
    <header
      class="fixed left-0 right-0 z-50 transition-all duration-500"
      style="top: var(--promo-bar-height, 0px)"
      [class]="scrolled()
        ? 'bg-surface-950/90 backdrop-blur-xl shadow-lg border-b border-white/10'
        : 'bg-transparent'"
    >
      <div class="mx-auto max-w-7xl px-6 lg:px-8">
        <div class="flex h-20 items-center justify-between">
          <!-- Logo -->
          <a routerLink="/" class="flex items-center gap-3 shrink-0 group">
            <div class="w-10 h-10 rounded-lg flex items-center justify-center transition-colors duration-300"
                 [class]="scrolled() ? 'bg-white/15 backdrop-blur-sm border border-white/20' : 'bg-white/15 backdrop-blur-sm border border-white/20'">
              <span class="text-white font-heading font-bold text-sm">{{ brand.logoInitials }}</span>
            </div>
            <div class="flex flex-col leading-tight">
              <span class="font-heading text-[1rem] font-bold tracking-[-0.02em] transition-colors duration-300"
                    [class]="scrolled() ? 'text-white' : 'text-white'">{{ brand.shortName }}</span>
              <span class="text-[0.6rem] font-semibold uppercase tracking-[0.2em] transition-colors duration-300"
                    [class]="scrolled() ? 'text-white/50' : 'text-white/50'">{{ brand.name.replace(brand.shortName, '').trim() || brand.tagline }}</span>
            </div>
          </a>

          <!-- Desktop nav -->
          <nav class="hidden lg:flex items-center gap-0.5" aria-label="Main navigation">
            @for (item of navItems; track item.label) {
              @if (item.children || item.megaColumns) {
                <!-- Item with dropdown / mega -->
                <div class="relative"
                     (mouseenter)="openMenu(item.label)"
                     (mouseleave)="closeMenuDelayed()">
                  <button
                    class="inline-flex items-center gap-1 px-4 py-2 text-[0.8125rem] font-medium rounded-lg transition-all duration-200"
                    [class]="scrolled()
                      ? 'text-white/75 hover:text-white hover:bg-white/[0.08]'
                      : 'text-white/75 hover:text-white hover:bg-white/[0.08]'"
                    (click)="toggleMenu(item.label)"
                  >
                    {{ item.label }}
                    <svg aria-hidden="true" class="w-3.5 h-3.5 transition-transform duration-200"
                         [class.rotate-180]="activeMenu() === item.label"
                         fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                    </svg>
                  </button>

                  <!-- DROPDOWN (small flyout) -->
                  @if (item.menuType === 'dropdown' && activeMenu() === item.label) {
                    <div class="absolute top-full left-0 pt-2 z-50"
                         (mouseenter)="cancelClose()" (mouseleave)="closeMenuDelayed()">
                      <div class="w-64 bg-white rounded-xl shadow-xl shadow-surface-900/[0.08] border border-surface-100 overflow-hidden animate-dropdown">
                        <div class="py-2">
                          @for (child of item.children; track child.path) {
                            <a [routerLink]="child.path"
                               (click)="closeMenu()"
                               class="flex items-start gap-3 px-4 py-3 hover:bg-surface-50 transition-colors group">
                              @if (child.icon) {
                                <span class="text-lg mt-0.5 shrink-0 opacity-60 group-hover:opacity-100 transition-opacity" [innerHTML]="child.icon"></span>
                              }
                              <div>
                                <div class="text-sm font-medium text-surface-900 group-hover:text-brand-700 transition-colors">{{ child.label }}</div>
                                @if (child.desc) {
                                  <div class="text-xs text-surface-400 mt-0.5 leading-relaxed">{{ child.desc }}</div>
                                }
                              </div>
                            </a>
                          }
                        </div>
                        @if (item.path) {
                          <div class="border-t border-surface-100 px-4 py-3 bg-surface-50/50">
                            <a [routerLink]="item.path" (click)="closeMenu()"
                               class="group inline-flex items-center gap-1.5 text-xs font-semibold text-brand-600 hover:text-brand-700 transition-colors">
                              View all {{ item.label }}
                              <svg aria-hidden="true" class="w-3 h-3 transition-transform group-hover:translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                            </a>
                          </div>
                        }
                      </div>
                    </div>
                  }

                  <!-- MEGA MENU (full-width panel) -->
                  @if (item.menuType === 'mega' && activeMenu() === item.label) {
                    <div class="fixed left-0 right-0 top-20 z-50"
                         (mouseenter)="cancelClose()" (mouseleave)="closeMenuDelayed()">
                      <div class="bg-white border-b border-surface-100 shadow-xl shadow-surface-900/[0.08] animate-mega">
                        <div class="mx-auto max-w-7xl px-6 lg:px-8 py-8">
                          <div class="grid gap-8" [style.grid-template-columns]="item.megaFeatured ? 'repeat(' + (item.megaColumns?.length || 1) + ', 1fr) 1.2fr' : 'repeat(' + (item.megaColumns?.length || 1) + ', 1fr)'">
                            @for (col of item.megaColumns; track col.heading) {
                              <div>
                                <h4 class="text-[0.6875rem] font-semibold uppercase tracking-[0.12em] text-surface-400 mb-4">{{ col.heading }}</h4>
                                <div class="space-y-1">
                                  @for (link of col.links; track link.path) {
                                    <a [routerLink]="link.path"
                                       (click)="closeMenu()"
                                       class="flex items-start gap-3 p-2.5 -mx-2.5 rounded-lg hover:bg-surface-50 transition-colors group">
                                      @if (link.icon) {
                                        <div class="w-9 h-9 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center shrink-0 group-hover:bg-brand-100 transition-colors">
                                          <span class="text-base" [innerHTML]="link.icon"></span>
                                        </div>
                                      }
                                      <div>
                                        <div class="text-sm font-medium text-surface-800 group-hover:text-brand-700 transition-colors">{{ link.label }}</div>
                                        @if (link.desc) {
                                          <div class="text-xs text-surface-400 mt-0.5 leading-relaxed">{{ link.desc }}</div>
                                        }
                                      </div>
                                    </a>
                                  }
                                </div>
                              </div>
                            }
                            <!-- Featured card -->
                            @if (item.megaFeatured) {
                              <a [routerLink]="item.megaFeatured.path"
                                 (click)="closeMenu()"
                                 class="rounded-xl overflow-hidden relative group"
                                 [style.background]="item.megaFeatured.gradient">
                                <div class="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors"></div>
                                <div class="relative p-6 flex flex-col justify-end h-full min-h-[200px]">
                                  <div class="text-[0.6rem] font-semibold uppercase tracking-[0.15em] text-white/60 mb-2">Featured</div>
                                  <h4 class="font-heading text-lg font-bold text-white mb-1">{{ item.megaFeatured.title }}</h4>
                                  <p class="text-sm text-white/70 leading-relaxed">{{ item.megaFeatured.desc }}</p>
                                  <span class="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-white group-hover:gap-2.5 transition-all">
                                    Learn more
                                    <svg aria-hidden="true" class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                                  </span>
                                </div>
                              </a>
                            }
                          </div>
                        </div>
                      </div>
                      <!-- Backdrop -->
                      <div class="fixed inset-0 -z-10" (click)="closeMenu()"></div>
                    </div>
                  }
                </div>
              } @else {
                <!-- Simple nav link -->
                <a
                  [routerLink]="item.path"
                  routerLinkActive="!font-semibold"
                  [routerLinkActiveOptions]="{ exact: item.path === '/' }"
                  class="relative px-4 py-2 text-[0.8125rem] font-medium rounded-lg transition-all duration-200"
                  [class]="scrolled()
                    ? 'text-white/75 hover:text-white hover:bg-white/[0.08]'
                    : 'text-white/75 hover:text-white hover:bg-white/[0.08]'"
                >
                  {{ item.label }}
                </a>
              }
            }
            <a routerLink="/contact"
               class="ml-4 inline-flex items-center gap-2 px-5 py-2 text-[0.8125rem] font-semibold rounded-full transition-all duration-300 bg-brand-500 text-white hover:bg-brand-400 hover:shadow-lg hover:shadow-brand-500/30 ring-1 ring-brand-400/30">
              Free Estimate
              <svg aria-hidden="true" class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
            </a>
          </nav>

          <!-- Mobile toggle -->
          <button
            class="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg transition-colors"
            [class]="scrolled() ? 'text-white hover:bg-white/10' : 'text-white hover:bg-white/10'"
            (click)="mobileOpen.set(!mobileOpen())"
            [attr.aria-expanded]="mobileOpen()"
            aria-label="Toggle navigation menu"
          >
            <svg aria-hidden="true" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
              @if (!mobileOpen()) {
                <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 9h16.5m-16.5 6.75h16.5" />
              } @else {
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
              }
            </svg>
          </button>
        </div>
      </div>

      <!-- Mobile nav -->
      @if (mobileOpen()) {
        <nav class="lg:hidden bg-surface-950/95 backdrop-blur-xl border-t border-white/10 shadow-xl max-h-[calc(100vh-5rem)] overflow-y-auto" aria-label="Mobile navigation">
          <div class="px-6 py-3 space-y-0.5">
            @for (item of navItems; track item.label) {
              @if (item.children || item.megaColumns) {
                <!-- Expandable mobile item -->
                <div>
                  <button (click)="toggleMobileExpand(item.label)"
                          class="flex items-center justify-between w-full rounded-lg px-4 py-3 text-[0.9375rem] font-medium text-white hover:bg-white/[0.08] transition-colors">
                    {{ item.label }}
                    <svg aria-hidden="true" class="w-4 h-4 text-white/40 transition-transform duration-200"
                         [class.rotate-180]="mobileExpanded() === item.label"
                         fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                    </svg>
                  </button>
                  @if (mobileExpanded() === item.label) {
                    <div class="pl-4 pb-2 space-y-0.5 animate-slideDown">
                      @if (item.children) {
                        @for (child of item.children; track child.path) {
                          <a [routerLink]="child.path" (click)="mobileOpen.set(false)"
                             class="flex items-center gap-3 rounded-lg px-4 py-2.5 text-sm text-white/70 hover:bg-white/[0.08] hover:text-white transition-colors">
                            @if (child.icon) {
                              <span class="text-base opacity-60" [innerHTML]="child.icon"></span>
                            }
                            {{ child.label }}
                          </a>
                        }
                      }
                      @if (item.megaColumns) {
                        @for (col of item.megaColumns; track col.heading) {
                          <div class="pt-2 first:pt-0">
                            <div class="px-4 py-1.5 text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-white/40">{{ col.heading }}</div>
                            @for (link of col.links; track link.path) {
                              <a [routerLink]="link.path" (click)="mobileOpen.set(false)"
                                 class="flex items-center gap-3 rounded-lg px-4 py-2.5 text-sm text-white/70 hover:bg-white/[0.08] hover:text-white transition-colors">
                                @if (link.icon) {
                                  <span class="text-base opacity-60" [innerHTML]="link.icon"></span>
                                }
                                {{ link.label }}
                              </a>
                            }
                          </div>
                        }
                      }
                      @if (item.path) {
                        <a [routerLink]="item.path" (click)="mobileOpen.set(false)"
                           class="flex items-center gap-1.5 rounded-lg px-4 py-2.5 text-sm font-semibold text-brand-600 hover:bg-brand-50 transition-colors">
                          View all {{ item.label }}
                          <svg aria-hidden="true" class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                        </a>
                      }
                    </div>
                  }
                </div>
              } @else {
                <a [routerLink]="item.path" (click)="mobileOpen.set(false)"
                   class="flex items-center rounded-lg px-4 py-3 text-[0.9375rem] font-medium text-white hover:bg-white/[0.08] transition-colors">
                  {{ item.label }}
                </a>
              }
            }
            <a routerLink="/contact" (click)="mobileOpen.set(false)"
               class="flex items-center justify-center rounded-full px-4 py-3 mt-2 bg-brand-500 text-white font-semibold text-sm">
              Free Estimate
            </a>
          </div>
        </nav>
      }
    </header>
    <!-- Spacer -->
    <div class="h-20"></div>

    <style>
      .animate-dropdown {
        animation: dropdownIn 0.2s cubic-bezier(0.22, 1, 0.36, 1);
      }
      .animate-mega {
        animation: megaIn 0.25s cubic-bezier(0.22, 1, 0.36, 1);
      }
      .animate-slideDown {
        animation: slideDown 0.2s cubic-bezier(0.22, 1, 0.36, 1);
      }
      @keyframes dropdownIn {
        from { opacity: 0; transform: translateY(-4px) scale(0.98); }
        to { opacity: 1; transform: translateY(0) scale(1); }
      }
      @keyframes megaIn {
        from { opacity: 0; transform: translateY(-8px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes slideDown {
        from { opacity: 0; max-height: 0; }
        to { opacity: 1; max-height: 500px; }
      }
    </style>
  `,
})
export class HeaderComponent {
  private el = inject(ElementRef);
  private config = inject(SiteConfigService);
  private closeTimeout: any = null;

  brand = this.config.brand;
  navItems = this.config.nav;

  scrolled = signal(false);
  mobileOpen = signal(false);
  activeMenu = signal<string | null>(null);
  mobileExpanded = signal<string | null>(null);

  @HostListener('window:scroll')
  onScroll() {
    if (typeof window !== 'undefined') {
      this.scrolled.set(window.scrollY > 30);
    }
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    this.closeMenu();
  }

  openMenu(label: string) {
    this.cancelClose();
    this.activeMenu.set(label);
  }

  closeMenu() {
    this.cancelClose();
    this.activeMenu.set(null);
  }

  closeMenuDelayed() {
    this.closeTimeout = setTimeout(() => this.activeMenu.set(null), 150);
  }

  cancelClose() {
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }
  }

  toggleMenu(label: string) {
    this.activeMenu.set(this.activeMenu() === label ? null : label);
  }

  toggleMobileExpand(label: string) {
    this.mobileExpanded.set(this.mobileExpanded() === label ? null : label);
  }
}
